import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import {
  Box,
  CircularProgress,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import Table from '@mui/material/Table';

import { routePaths } from 'src/services/routing/routePaths';
import { useTicketTableContext } from 'src/components/followUp/TicketFilterContext';

import TicketDetailModal from '../TicketDetail/TicketDetailModal';

import HeaderTableCell from './Cells/HeaderCell';
import TicketTablePagination from './TicketTablePagination/TicketTablePagination';
import { TICKETS_PER_PAGE } from './TicketTablePagination/useTicketTablePagination';
import ErrorRow from './ErrorRow';
import TicketTableRow from './TicketTableRow';
import TicketTableToolbar from './TicketTableToolbar';

import { styles } from './table.styles';

interface TicketTableProps {}

const TableHeaderMap: Record<string, { columnKey: string; minWidth: string }> =
  {
    id: { columnKey: 'id', minWidth: '7ch' },
    npsGroup: { columnKey: 'nps_group', minWidth: '9ch' },
    nps: { columnKey: 'nps_score', minWidth: '6ch' },
    respondent: { columnKey: 'respondent__name', minWidth: '18ch' },
    followUpOwner: {
      columnKey: 'follow_up_owner',
      minWidth: '18ch',
    },
    created: { columnKey: 'created_at', minWidth: '8ch' },
    lastUpdated: { columnKey: 'last_modified_at', minWidth: '8ch' },
    firstContact: { columnKey: 'first_contact_or_due_at', minWidth: '8ch' },
    status: { columnKey: 'status', minWidth: '9ch' },
  };

const TicketTable: FC<TicketTableProps> = () => {
  const history = useHistory();
  const { t } = useTranslation('followUp');
  const {
    listIsLoading: loading,
    page,
    setPage,
    tickets,
    totalTickets,
    listError,
  } = useTicketTableContext();
  const emptyRows = tickets ? TICKETS_PER_PAGE - tickets.length : 0;

  return (
    <Box sx={(theme) => ({ marginX: theme.spacing(4) })}>
      <TicketDetailModal />
      <TicketTableToolbar />
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Box sx={{ overflow: 'scroll', maxWidth: '100%' }}>
          <Table>
            <TableHead
              sx={(theme) => ({ backgroundColor: theme.colors.neutral[50] })}
            >
              <TableRow>
                {Object.entries(TableHeaderMap).map(
                  ([translationKey, { columnKey, minWidth }]) => (
                    <HeaderTableCell
                      key={columnKey}
                      columnKey={columnKey}
                      minWidth={minWidth}
                    >
                      {t(`tableHeader.${translationKey}`)}
                    </HeaderTableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {listError ? (
                <ErrorRow text={listError} />
              ) : !tickets || loading ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    sx={{
                      textJustify: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <CircularProgress aria-label="spinner" />
                  </TableCell>
                </TableRow>
              ) : tickets.length ? (
                tickets.map((ticket) => (
                  <TicketTableRow
                    key={ticket.id}
                    {...ticket}
                    onClick={() => {
                      history.push({
                        pathname: routePaths.TICKET_OVERVIEW,
                        search: `?ticket=${ticket.id}`,
                      });
                    }}
                  />
                ))
              ) : (
                <ErrorRow text={t('noTicketsFound')} />
              )}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 48 * emptyRows,
                  }}
                >
                  <TableCell colSpan={9} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        <TicketTablePagination
          page={page}
          setPage={setPage}
          totalTickets={totalTickets ?? 0}
        />
      </TableContainer>
    </Box>
  );
};

export default TicketTable;
